import {
  PlatformCrossSellChatDeliveryStatus,
  PlatformCrossSellSmsDeliveryStatus,
  PlatformCrossSellTargetProgram,
} from '@web-app/generated/platform/graphql';

export type CrossSellBookingLinkModel =
  | { status: 'hidden' }
  | { status: 'loading' }
  | {
      status: 'ready' | 'sent';
      targetProgram: PlatformCrossSellTargetProgram;
      programName: string;
      description: string;
      sendButtonLabel: string;
      /** When the link reached the patient; null until a channel completes. */
      sentAt: Date | null;
    };

type ReadyCrossSellBookingLinkModel = Extract<
  CrossSellBookingLinkModel,
  { status: 'ready' | 'sent' }
>;

const PROGRAM_CONTENT = {
  [PlatformCrossSellTargetProgram.Glp_1]: {
    programName: 'Berry Street GLP-1 Program',
    description:
      'Berry Street prescribers now offer GLP-1 medication alongside your nutrition visits, all on Berry Street. Send a link if it comes up.',
    sendButtonLabel: 'Send GLP-1 booking link',
  },
  [PlatformCrossSellTargetProgram.Mnt]: {
    programName: 'Medical nutrition therapy',
    description:
      'Patients combining GLP-1 treatment with nutritional support tend to see better long-term results. Send them a link to their nutrition session before they leave',
    sendButtonLabel: 'Send MNT booking link',
  },
} satisfies Record<
  PlatformCrossSellTargetProgram,
  Omit<ReadyCrossSellBookingLinkModel, 'status' | 'targetProgram' | 'sentAt'>
>;

type ResolveCrossSellBookingLinkModelInput = {
  hasRequiredIds: boolean;
  isLoading: boolean;
  isError: boolean;
  targetProgram?: PlatformCrossSellTargetProgram | null;
  chatDeliveryCompleted?: boolean;
  smsDeliveryCompleted?: boolean;
  /** ISO timestamp from Platform; tRPC delivers it as a string. */
  sentAt?: string | null;
};

/** Converts transport states into the small set of states the card can render. */
export function resolveCrossSellBookingLinkModel({
  hasRequiredIds,
  isLoading,
  isError,
  targetProgram,
  chatDeliveryCompleted,
  smsDeliveryCompleted,
  sentAt,
}: ResolveCrossSellBookingLinkModelInput): CrossSellBookingLinkModel {
  if (!hasRequiredIds || isError) {
    return { status: 'hidden' };
  }

  if (isLoading) {
    return { status: 'loading' };
  }

  if (targetProgram) {
    return {
      status: chatDeliveryCompleted && smsDeliveryCompleted ? 'sent' : 'ready',
      targetProgram,
      ...PROGRAM_CONTENT[targetProgram],
      sentAt: sentAt ? new Date(sentAt) : null,
    };
  }

  return { status: 'hidden' };
}

/** What the provider is told after a send, and which channels to report. */
export type CrossSellSendOutcome = {
  kind: 'sent' | 'smsOnly' | 'pending' | 'partial' | 'failed';
  chatAccepted: boolean;
  smsAccepted: boolean;
};

/**
 * `ALREADY_PROCESSED` counts as accepted: an earlier attempt of this same send
 * reached the vendor, so the patient has the link either way. `IN_FLIGHT` is a
 * concurrent attempt still running, which is neither a success to celebrate nor
 * a failure to retry.
 */
export function resolveCrossSellSendOutcome(result: {
  chatStatus: PlatformCrossSellChatDeliveryStatus;
  smsStatus: PlatformCrossSellSmsDeliveryStatus;
}): CrossSellSendOutcome {
  const chatSkipped =
    result.chatStatus === PlatformCrossSellChatDeliveryStatus.Skipped;
  const chatAccepted =
    result.chatStatus === PlatformCrossSellChatDeliveryStatus.Sent ||
    result.chatStatus === PlatformCrossSellChatDeliveryStatus.AlreadyProcessed;
  const smsAccepted =
    result.smsStatus === PlatformCrossSellSmsDeliveryStatus.EventAccepted ||
    result.smsStatus === PlatformCrossSellSmsDeliveryStatus.AlreadyProcessed;

  const kind = ((): CrossSellSendOutcome['kind'] => {
    if (chatAccepted && smsAccepted) return 'sent';
    if (chatSkipped && smsAccepted) return 'smsOnly';
    if (
      result.chatStatus === PlatformCrossSellChatDeliveryStatus.InFlight ||
      result.smsStatus === PlatformCrossSellSmsDeliveryStatus.InFlight
    ) {
      return 'pending';
    }
    if (chatSkipped) return 'failed';
    return chatAccepted || smsAccepted ? 'partial' : 'failed';
  })();

  return { kind, chatAccepted, smsAccepted };
}
